import { Fieldset } from '@headlessui/react';
import classNames from 'classnames';
import { Children, Fragment, isValidElement, ReactNode } from 'react';
import { FormCell } from './FormCell';
import { FormRow } from './FormRow';

interface FormSectionProps {
  componentsPerLine: number;
  children: ReactNode;
  className?: string;
}

const layoutByComponentsPerLine: Record<number, { gridCols: string; childSpan: string }> = {
  1: { gridCols: 'md:grid-cols-12', childSpan: 'md:col-span-12' },
  2: { gridCols: 'md:grid-cols-12', childSpan: 'md:col-span-6' },
  3: { gridCols: 'md:grid-cols-12', childSpan: 'md:col-span-4' },
  4: { gridCols: 'md:grid-cols-12', childSpan: 'md:col-span-3' },
  5: { gridCols: 'md:grid-cols-5', childSpan: 'md:col-span-1' },
};

const hasRenderableContent = (node: ReactNode): boolean => {
  if (node == null || typeof node === 'boolean') return false;
  if (Array.isArray(node)) return node.some(hasRenderableContent);
  if (
    isValidElement<{ children?: ReactNode }>(node) &&
    (node.type === Fragment || node.type === FormRow || node.type === FormCell)
  ) {
    return hasRenderableContent(node.props.children);
  }
  return true;
};

const renderGridChildren = (children: ReactNode, childSpan: string): ReactNode =>
  Children.map(children, (child) => {
    // React does not render nullish values or booleans, so they must not take
    // up a grid cell. These commonly come from conditional field expressions.
    if (child == null || typeof child === 'boolean') {
      return null;
    }

    // A Fragment is a grouping construct rather than a field. Recursively
    // lay out its children so nested/conditional fields remain direct grid
    // items in their original order without an extra empty wrapper cell.
    if (isValidElement<{ children?: ReactNode }>(child) && child.type === Fragment) {
      return renderGridChildren(child.props.children, childSpan);
    }

    // Conditional content is sometimes placed *inside* a FormRow/FormCell.
    // Omitting that empty grid item lets the remaining desktop fields reflow
    // instead of leaving a phantom column or full blank row behind.
    if (
      isValidElement<{ children?: ReactNode }>(child) &&
      (child.type === FormRow || child.type === FormCell) &&
      !hasRenderableContent(child.props.children)
    ) {
      return null;
    }

    // [MCV-1529] A `FormCell` child already renders its OWN span-driven
    // wrapper div (see FormCell.tsx) -- wrapping it again here would nest
    // two `md:col-span-*` divs, and the OUTER one would still force the
    // shared `childSpan`, silently overriding the cell's own `span`.
    return isValidElement(child) && child.type === FormCell ? (
      child
    ) : (
      <div className={classNames('min-w-0', childSpan)}>{child}</div>
    );
  });

export const FormSection = (props: FormSectionProps) => {
  const { children, componentsPerLine } = props;
  const layout = layoutByComponentsPerLine[componentsPerLine] ?? layoutByComponentsPerLine[1];

  return (
    <Fieldset
      className={classNames(
        'grid grid-cols-1 gap-x-6 gap-y-5 text-grey-100',
        layout.gridCols,
        props.className
      )}
    >
      {renderGridChildren(children, layout.childSpan)}
    </Fieldset>
  );
};
